import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Bot, Trash2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AiChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get('/ai/student-chat/history')
      .then(r => setMessages(r.data.messages || []))
      .catch(() => toast.error("Erreur lors du chargement de l'historique"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    const tempId = Date.now();
    const tempUserMsg = { id: tempId, role: 'user', content: trimmed, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);
    setInput("");
    setSending(true);

    try {
      const { data } = await api.post('/ai/student-chat', { message: trimmed });
      setMessages(prev => [
        ...prev.filter(m => m.id !== tempId),
        data.user_message,
        data.assistant_message,
      ]);
    } catch {
      setMessages(prev => prev.filter(m => m.id !== tempId));
      toast.error("Impossible d'obtenir une réponse");
    } finally {
      setSending(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Effacer tout l'historique de conversation ?")) return;
    try {
      await api.delete('/ai/student-chat/history');
      setMessages([]);
      toast.success("Conversation effacée");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const suggestions = [
    "Qu'est-ce que je mange demain ?",
    "Combien de protéines par jour ?",
    "Mon entraînement de cette semaine ?",
  ];

  return (
    <div className="h-[calc(100vh-200px)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">
              Assistant IA
            </h1>
            <p className="text-sm text-slate-400">
              Pose tes questions sur ta nutrition et ton entraînement
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <Button variant="outline" size="sm" onClick={handleClear} className="text-slate-500">
            <Trash2 className="w-4 h-4 mr-1" /> Nouvelle conversation
          </Button>
        )}
      </div>

      {/* Chat container */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col h-[calc(100%-80px)]">
        {/* Chat header */}
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-purple-100 flex items-center justify-center">
            <Bot className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="font-medium text-slate-900">Coach IA</p>
            <p className="text-xs text-slate-400">Disponible 24/7</p>
          </div>
        </div>

        {/* Messages area */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-3">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
              </div>
            ) : messages.length === 0 && !sending ? (
              <div className="text-center py-12 space-y-3">
                <Bot className="w-12 h-12 text-slate-200 mx-auto" />
                <p className="text-slate-400 text-sm">
                  Salut ! Je suis ton assistant IA personnel.
                </p>
                <p className="text-slate-400 text-sm">
                  Pose-moi une question sur ton plan diet, ton entraînement ou ta progression !
                </p>
                <div className="flex flex-wrap justify-center gap-2 pt-4">
                  {suggestions.map(s => (
                    <button
                      key={s}
                      onClick={() => setInput(s)}
                      className="px-3 py-1.5 rounded-full bg-purple-50 text-purple-600 text-xs font-medium hover:bg-purple-100 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map(m => (
                <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {m.role === 'assistant' && (
                    <div className="h-7 w-7 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                      <Bot className="w-4 h-4 text-purple-600" />
                    </div>
                  )}
                  <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-slate-100 text-slate-900 rounded-bl-md'
                  }`}>
                    {m.content}
                    <p className={`text-[10px] mt-1 ${m.role === 'user' ? 'text-blue-200' : 'text-slate-400'}`}>
                      {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))
            )}

            {sending && (
              <div className="flex justify-start">
                <div className="h-7 w-7 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                  <Bot className="w-4 h-4 text-purple-600" />
                </div>
                <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </ScrollArea>

        {/* Input form */}
        <form onSubmit={handleSend} className="p-4 border-t border-slate-100 flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Pose ta question..."
            className="flex-1 h-11 rounded-xl border border-slate-200 px-4 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
            disabled={sending}
            maxLength={2000}
          />
          <Button
            type="submit"
            disabled={sending || !input.trim()}
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl h-11 px-5"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      </div>
    </div>
  );
}
